/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
import {
  Calculator,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Layers,
  Ruler,
  Truck,
  Wrench,
  X,
} from "lucide-react";
import type { SolutionTemplateConfig } from "@/components/landing/solution-template";
import {
  CTA,
  AI_TOOL_COUNT,
  STARTING_PRICE,
} from "@/content/landing/constants";

export const concreteConfig: SolutionTemplateConfig = {
  tradeName: "Concrete",
  tradeSlug: "concrete",
  hero: {
    badge: "Concrete Business Platform",
    title: "Software for concrete contractors",
    titleHighlight: "from pour to paid.",
    description:
      "What's your actual cost per yard including pump and finish? Are your flatwork margins different from your structural margins? One platform for your costs, estimates, projects, equipment, and invoices.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded concrete costs",
      "Cancel anytime",
    ],
  },
  trustBar: {
    headline: "Built for concrete contractors",
    proofPoints: [
      { icon: Ruler, text: "Flatwork, structural, and decorative" },
      { icon: DollarSign, text: "Yardage, rebar, and form costs" },
      { icon: Truck, text: "Equipment tracking (pumps, forms, finishing)" },
    ],
  },
  painPoints: {
    title: "Does this sound familiar?",
    description:
      "The daily reality of running a concrete business without a system.",
    items: [
      {
        icon: Clock,
        title: "Yardage calculations on a notepad",
        description:
          "Length x width x depth ÷ 27. You do it in your head or on a scrap of paper. But when the yardage is wrong, you're either paying for an extra truck or coming up short on the pour.",
      },
      {
        icon: DollarSign,
        title: "Pump truck and equipment costs buried in overhead",
        description:
          "Pump truck rental, finishing equipment, forms, vibrators — these are real job costs but they're not tracked per project. You don't know if the pump cost ate your margin until the season's over.",
      },
      {
        icon: FileText,
        title: "Sub work with no visibility",
        description:
          "You sub out pumping or finishing on some jobs and self-perform on others. But the cost difference isn't tracked. You don't know which approach is more profitable.",
      },
    ],
  },
  stats: [
    { value: "Cost per yard", label: "Tracked on every pour" },
    { value: `${AI_TOOL_COUNT} tools`, label: "AI assistant for your data" },
    { value: `$${STARTING_PRICE}/mo`, label: "Starting price" },
  ],
  howItWorks: {
    title: "The full lifecycle — one platform",
    description: "From structural plans to final invoice.",
    steps: [
      {
        step: 1,
        title: "Set up your business",
        description:
          "Configure yardage costs by mix design, rebar pricing by size, form costs, and finishing crew rates. Build work packages — a slab pour bundle (concrete + rebar + forms + finish + pump) you reuse on every job. Track equipment costs for pump trucks and finishing tools.",
        icon: Ruler,
      },
      {
        step: 2,
        title: "Win the work",
        description:
          "Upload foundation or flatwork plans, measure areas and depths, calculate yardage. Organize by pour section. Rebar, forms, concrete, pump truck, and finishing labor all tracked. Send a branded proposal with pour quantities and material breakdown.",
        icon: Calculator,
      },
      {
        step: 3,
        title: "Run the job",
        description:
          "Your accepted proposal becomes a project. Track crew hours per pour, log actual yardage delivered, manage equipment costs. If you sub out pumping or finishing, track those costs through supplier management.",
        icon: Wrench,
      },
      {
        step: 4,
        title: "Know your numbers",
        description:
          "Actual costs feed back into your cost database. Compare flatwork vs structural profitability. Ask Hank: 'What's my average cost per yard on flatwork jobs this year?'",
        icon: DollarSign,
      },
    ],
  },
  features: {
    title: "What you actually get",
    description:
      "Not generic construction software. A platform that understands yardage, rebar, forms, and the pump truck question.",
    items: [
      {
        icon: Layers,
        title: "Work packages for common pours",
        description:
          "Slab pour (concrete + rebar + forms + finish + pump), foundation wall (forms + rebar + concrete + stripping + waterproofing), sidewalk (concrete + base + forms + finish + sawcuts).",
        bullets: [
          "Material + labor + equipment bundles",
          "Configurable by mix design and pour type",
          "Track usage across jobs",
          "Waste factor for concrete",
        ],
      },
      {
        icon: Truck,
        title: "Equipment and sub tracking",
        description:
          "Pump trucks, finishing equipment, forms — real job costs tracked per project. When you sub out pumping, track the cost through supplier management so you can compare self-perform vs sub pricing.",
        bullets: [
          "Equipment TCO tracking",
          "Per-project equipment costs",
          "Subcontractor management",
          "Self-perform vs sub comparison",
        ],
      },
      {
        icon: Ruler,
        title: "Blueprint takeoffs for concrete plans",
        description:
          "Upload structural or site plans and measure areas, depths, and linear features. Calculate yardage directly. Quantities feed into your estimate through work packages.",
        bullets: [
          "Measure areas and depths on plans",
          "Automatic yardage calculation",
          "Rebar quantity estimation",
          "Multi-page plan support",
        ],
      },
      {
        icon: DollarSign,
        title: "Budget vs. actuals per pour",
        description:
          "Track actual yardage delivered, actual pump costs, actual finishing hours — all against the original estimate. Know your real cost per yard before the job closes.",
        bullets: [
          "Actual costs logged against estimate line items",
          "Per-pour cost tracking",
          "Variance reporting",
          "Feeds back into cost database",
        ],
      },
    ],
  },
  beforeAfter: {
    title: "The old way vs. the CC way",
    before: [
      { icon: X, text: "Yardage calculations on a notepad" },
      { icon: X, text: "Pump truck costs buried in overhead" },
      { icon: X, text: "No tracking of sub vs self-perform profitability" },
      { icon: X, text: "Rebar quantities from experience" },
      { icon: X, text: "Proposals that take days" },
      { icon: X, text: "Crew hours tracked on paper" },
    ],
    after: [
      { icon: CheckCircle, text: "Yardage calculated from plan measurements" },
      { icon: CheckCircle, text: "Equipment costs tracked per project" },
      { icon: CheckCircle, text: "Sub vs self-perform costs compared" },
      { icon: CheckCircle, text: "Budget vs. actuals on every pour" },
      { icon: CheckCircle, text: "Branded proposals with pour quantities" },
      { icon: CheckCircle, text: "Time entries with approval workflow" },
    ],
  },
  roi: {
    title: "What changes when your business is connected",
    description:
      "When your yardage calculations feed into estimates, your actuals feed back, and your equipment costs are tracked per project.",
    metrics: [
      { value: "Accurate", label: "Cost per yard from actual job data" },
      { value: "Visible", label: "Equipment and sub costs per project" },
      { value: "Connected", label: "Estimate → project → invoice in one flow" },
    ],
    ctaText: CTA.PRIMARY,
  },
  faq: {
    title: "Questions concrete contractors ask",
    items: [
      {
        question: "Does it calculate yardage?",
        answer:
          "You measure areas and depths from blueprints and the estimate tracks total yardage per pour section. Work packages include concrete by the yard with waste factor.",
      },
      {
        question: "Can I track pump truck costs per job?",
        answer:
          "Yes. Equipment costs are tracked per project. You can see exactly what the pump cost on each job and whether it ate your margin.",
      },
      {
        question: "Does it handle subcontractor work?",
        answer:
          "Yes. Track sub costs through supplier management. Compare self-perform vs sub pricing to see which approach is more profitable.",
      },
      {
        question: "How do work packages work for concrete?",
        answer:
          "Build reusable bundles — a 'Slab Pour' package includes concrete, rebar, forms, finishing labor, and pump. Pull it into any estimate and adjust quantities for the specific pour.",
      },
      {
        question: "Can I track crew hours?",
        answer:
          "Yes. Time entries with approval workflow. Track pour crews and finishing crews separately.",
      },
      {
        question: "Can my team use it?",
        answer:
          "Yes. Add estimators, foremen, or office staff. Role-based access.",
      },
    ],
  },
  cta: {
    title: "Run your concrete business from one platform.",
    description:
      "Yardage tracking, equipment costs, sub management, invoicing — connected. 7-day free trial.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded concrete costs",
      "Cancel anytime",
    ],
  },
};
